import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchDataWithoutParams,
  fetchData,
  fetchDataWithParams,
  fetchFilters,
  fetchMultipleData,
} from 'src/api/filterApi';
import { AppConfig } from 'src/config/AppConfig';
import { Config } from 'src/context/ConfigContext';
import axios from 'axios';
import { UrlMapper } from 'src/utils/DataMappings';
import { getDateRange } from 'src/utils/DateUtil';
import { logout } from 'src/app/actions';
import {
  rejectValue,
  rejectWithValue,
  TransactionRequest,
  TransactionResponse,
} from 'src/types/transactions';
import { getAllActiveUsers, getInvitation } from 'src/config/firebaseConfig';
import { User, UserCollection } from 'src/types/user';
import { transformUserCollection } from 'src/utils/Utils';

export interface FilterData {
  id: string;
  label: string;
  listOfStores: string[];
}

export interface FilterStoreData {
  id: string;
  channel: string;
  locationName: string;
  machineType: string;
  planogram: string;
  products: string[];
}
export interface FilterProductData {
  id: string;
  name: string;
  sku: string;
  listOfStores: string[];
}

export interface StoreDetail {
  concept_name: string;
  estation_name: string;
}

export interface FilterState {
  channels: Record<string, FilterData[]>;
  machineTypes: Record<string, FilterData[]>;
  planograms: Record<string, FilterData[]>;
  locations: Record<string, FilterStoreData>;
  products: Record<string, FilterProductData>;
  clientConfig: Record<string, any>;
  loading: boolean;
  error: string | null;
  config: Config | null;
  selectedFilter: Record<string, string>;
  clientType: string;
  transactions: Record<string, TransactionResponse>;
  interactions: Record<string, any>;
  netOps: Record<string, any>;
  inventory: Record<string, any>;
  stores: Record<string, any>;
  users: UserCollection | null;
  admin: UserCollection | null;
  reports: Record<string, any>;
  showTable: boolean;
  isExportToCsv: boolean;
  showTrendLine: boolean;
  invite: Record<string, any>;
  storeIdDetails: StoreDetail[];
}

const initialState: FilterState = {
  channels: {},
  machineTypes: {},
  planograms: {},
  locations: {},
  loading: false,
  error: null,
  config: null,
  clientConfig: {},
  selectedFilter: {
    account: '',
    requester: '',
    start: '',
    end: '',
    report: '',
    timeStamp: 'storeLocal',
    channel: '',
    machine_type: '',
    operating_hours: '00:00:00, 23:59:00',
    products: '',
    storeStatus: '',
    sort: '',
    page: '1',
    store: '',
    per_page: '100',
    aggregation: '',
    groupBy: '',
    priority: '',
    ticketType: '',
    storeId:'',
    alertId:'',
    //brands:'',
    brands:'iStore_Swyft',
  },
  clientType: localStorage.getItem('clientType') || 'Brand',
  products: {},
  transactions: {},
  interactions: {},
  netOps: {},
  inventory: {
    data: [],
    pagination: {
      current_page: 1,
      rows_total: 0,
      per_page: 100,
    },
  },
  stores: {
    data: [],
    pagination: {
      current_page: 1,
      rows_total: 0,
      per_page: 100,
    },
  },
  users: null,
  admin: {},
  reports: {},
  showTable: false,
  isExportToCsv: false,
  showTrendLine: false,
  invite: {},
  storeIdDetails: []
};

// Async action to fetch filters
export const getFilters = createAsyncThunk(
  'filters/getFilters',
  async (
    {
      url,
      headers,
      type,
    }: { url: string; headers: Record<string, string>; type: string },
    { rejectWithValue },
  ) => {
    const response = await fetchFilters(url, headers);
    if (!response || !response.data) {
      return rejectWithValue('No data Found');
    }
    return response.data;
  },
);

export const getActiveUsers = createAsyncThunk<
  UserCollection,
  { selectedMenu: string }
>(
  'filters/getActiveUsers',
  async ({ selectedMenu }: { selectedMenu: string }, {}) => {
    const response = await getAllActiveUsers();
    return response;
  },
);

export const getInvitationById = createAsyncThunk<
  User,
  { selectedMenu: string }
>(
  'filters/getInActiveUser',
  async ({ activationCode }: { activationCode: string }, {}) => {
    const response = await getInvitation(activationCode);
    return response;
  },
);

export const getTransactionData = createAsyncThunk<
  TransactionResponse[],
  TransactionRequest,
  { rejectValue: string }
>(
  'filters/getTransactionsData',
  async (
    { url, headers, type }: TransactionRequest,
    { getState, rejectWithValue },
  ) => {
    const { selectedFilter, transactions } = getState().filters;
    if (transactions && Object.keys(transactions).length > 0) {
      //return transactions
    }
    const {
      account,
      start,
      end,
      aggregation,
      requester,
      channel,
      machine_type,
      timeStamp,
      store,
    } = selectedFilter;

    const requests = AppConfig.transactions.map((report) => {
      let fUrl = url;
      console.log(fUrl);
      if (report === 'prodtransactions' || report == 'prodrevenue') {
        fUrl = fUrl.replace('dash_transactions', 'dash_transactions_sku');
      } else if (report === 'transactions') {
        fUrl = fUrl.replace('dash_transactions', 'dash_interactions');
      }
      const params = {
        account,
        start,
        end,
        report,
        aggregation,
        requester,
        channel,
        machine_type,
        store,
        timeStamp,
      };
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(
          ([_, value]) => value != null && value !== '',
        ),
      );
      return {
        url: fUrl,
        headers,
        params: filteredParams,
      };
    });
    try {
      const response: TransactionResponse[] = await fetchMultipleData(requests);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);

export const getInterActionsData = createAsyncThunk(
  'filters/getInterActionsData',
  async (
    {
      url,
      headers,
      type,
    }: { url: string; headers: Record<string, string>; type: string },
    { getState, rejectWithValue },
  ) => {
    const { selectedFilter, interactions } = getState().filters;
    if (interactions && Object.keys(interactions).length > 0) {
      // return interactions
    }
    const {
      account,
      start,
      end,
      aggregation,
      requester,
      channel,
      machine_type,
      store,
      timeStamp,
    } = selectedFilter;

    const requests = AppConfig.interActions.map((report) => {
      let fUrl = url;
      console.log(fUrl);
      if (report === 'timeofday' || report == 'timeofweek') {
        fUrl = fUrl.replace('dash_interactions', 'dash_interactions_time');
      } else if (report == 'pagenavigation') {
        fUrl = fUrl.replace('dash_interactions', 'dash_userpage_navigation');
      }
      const params = {
        account,
        start,
        end,
        report,
        aggregation,
        requester,
        channel,
        machine_type,
        store,
        timeStamp,
      };
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(
          ([_, value]) => value != null && value !== '',
        ),
      );
      return {
        url: fUrl,
        headers,
        params: filteredParams,
      };
    });
    try {
      const response = await fetchMultipleData(requests);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);
export const getNetOpsData = createAsyncThunk(
  'filters/getNetOpsData',
  async (
    {
      url,
      headers,
    }: { url: string; headers: Record<string, string>; type: string },
    { getState, rejectWithValue },
  ) => {
    const { selectedFilter, netOps } = getState().filters;
    // if (netOps && Object.keys(netOps).length > 0) {
    //   return netOps;
    // }
    const {
      account,
      start,
      end,
      aggregation,
      requester,
      channel,
      machine_type,
      store,
      timeStamp,
      operating_hours,
    } = selectedFilter;

    const params = {
      account,
      start,
      end,
      aggregation,
      requester,
      channel,
      machine_type,
      store,
      operating_hours,
      timeStamp,
    };
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value != null && value !== '',
      ),
    );

    const requests = AppConfig.netOps.map((report) => {
      const params = {...filteredParams}
      params['report']= report
      return {
        url,
        headers,
        params: params,
      };
    });
    try {
      const response = await fetchMultipleData(requests);
      console.log('all resposne--', response);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);
export const getInventoryData = createAsyncThunk(
  'filters/getInventoryData',
  async (
    {
      url,
      headers,
      page,
      selectedMenu,
    }: {
      url: string;
      headers: Record<string, string>;
      page: number;
      selectedMenu: string;
    },
    { getState, rejectWithValue },
  ) => {
    const filterState = getState().filters;
    const { selectedFilter } = filterState;
    const data = filterState[selectedMenu];
    let {
      account,
      sort,
      requester,
      channel,
      machine_type,
      store,
      timeStamp,
    } = selectedFilter;

    if (selectedMenu === 'inventory') {
      sort = 'store';
    }

    const params = {
      account,
      page,
      sort,
      requester,
      channel,
      machine_type,
      store,
      timeStamp,
    };

    try {
      // if (data && data.data && data.data.length > 0) {
      //   return data;
      // }
      const response = await fetchDataWithParams(url, headers, params);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);

export const getReportsData = createAsyncThunk(
  'filters/getReportsData',
  async (
    {
      url,
      headers,
      page,
      selectedMenu,
      selectedTile,
      issaleZero,
    }: {
      url: string;
      headers: Record<string, string>;
      page: number;
      selectedMenu: string;
      selectedTile: string;
      issaleZero: boolean;
    },
    { getState, rejectWithValue },
  ) => {
    const filterState = getState().filters;
    const { selectedFilter } = filterState;
    const data =
      filterState[selectedMenu] && filterState[selectedMenu][selectedTile]
        ? filterState[selectedMenu][selectedTile]
        : {};
    const {
      account,
      start,
      end,
      sort,
      requester,
      channel,
      machine_type,
      store,
      timeStamp,
      groupBy,
      aggregation,
    } = selectedFilter;
    let startDate = start;
    const params = {
      account,
      page,
      sort,
      start: startDate,
      end,
      requester,
      channel,
      machine_type,
      store,
      timeStamp,
      salesAmount: issaleZero ? 0 : 1,
    };
    if (
      selectedTile === AppConfig.reports[0] ||
      selectedTile === AppConfig.reports[1] ||
      selectedTile === AppConfig.reports[5]
    ) {
      params['additional_fields'] = 'city, state, postal_code, country_code';
    }
    if (
      selectedTile === AppConfig.reports[0] ||
      selectedTile === AppConfig.reports[2] ||
      selectedTile === AppConfig.reports[3]
    ) {
      params['group_by'] = groupBy;
    }
    if (
      selectedTile === AppConfig.reports[2] ||
      selectedTile === AppConfig.reports[7] ||
      selectedTile === AppConfig.reports[6]
    ) {
      params['aggregation'] = AppConfig.reportAggregationById[aggregation];
    }

    try {
      // if (data && data.data && data.data.length > 0) {
      //   return data;
      // }
      const response = await fetchDataWithParams(url, headers, params);
      console.log('inventory resposne--', response);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);

export const getStoreIds = createAsyncThunk(
  'filters/getStoreIds',
  async (
    {
      url,
      headers,
      brandsAsList,
    }: {
      url: string;
      headers: Record<string, string>;
      brandsAsList: any[]; 
    },
    { getState, rejectWithValue },
  ) => {
    const filterState = getState().filters;
    const { selectedFilter } = filterState;

    //const data = filterState[selectedMenu];
    const {
      account,
      start,
      end,
      sort,
      requester,
      //channel,
      //machine_type,
      //store,
      timeStamp,
    } = selectedFilter;
    const params = {};
    
    try {
      const response = await fetchDataWithoutParams(url, headers);
      console.log('StoreIds logs resposne--', response);
      console.log('Brands as List:', brandsAsList);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);

export const getReportsNMSWorker = createAsyncThunk(
  'filters/getReportsNMSWorker',
  async (
    {
      url,
      headers,
      page,
      selectedMenu,
    }: {
      url: string;
      headers: Record<string, string>;
      page: number;
      selectedMenu: string;
    },
    { getState, rejectWithValue },
  ) => {
    const filterState = getState().filters;
    const { selectedFilter } = filterState;

    const data = filterState[selectedMenu];
    const {
      account,
      start,
      end,
      sort,
      requester,
      //channel,
      //machine_type,
      //store,
      timeStamp,
      brands,
    } = selectedFilter;
    let startDate = start;
    const params = {
      //check if account to be assigned blank for actual api?
      // account:'',
      account: brands,
      page,
      sort,
      start: startDate,
      end,
      requester,
      //channel,
      //machine_type,
      //store,
      timeStamp,
    };
    
    try {
      const response = await fetchDataWithParams(url, headers, params);
      console.log('NMS Worker logs resposne--', response);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);

export const getNmsAlertsZendeskData = createAsyncThunk(
  'filters/getNmsAlertsZendeskData',
  async (
    {
      url,
      headers,
      page,
      selectedMenu,
    }: {
      url: string;
      headers: Record<string, string>;
      page: number;
      selectedMenu: string;
    },
    { getState, rejectWithValue },
  ) => {
    const filterState = getState().filters;
    const { selectedFilter } = filterState;

    const data = filterState[selectedMenu];
    const {
      account,
      start,
      end,
      sort,
      requester,
      priority,
      ticketType,
      storeId,
      alertId,
      brands,
      store,
      timeStamp,
    } = selectedFilter;
    let startDate = start;
    const params = {
      account: brands,
      //account,
      page,
      sort,
      start: startDate,
      end,
      requester,
      //priority,
      // ticketType,
      ticket_type: ticketType,
      ticket_priority: priority,
      storeId,
      alertId,
      brands,
      store,
      timeStamp,
    };
    
    try {
      const response = await fetchDataWithParams(url, headers, params);
      console.log('Nms Alerts : Zendesk resposne--', response);

            // Adjust the ticket_created field as per the specified conditions
            const dataArray = Array.isArray(response.data) ? response.data : [];

            const adjustedData = dataArray.map((item: any) => ({
              ...item,
              ticket_created: item.created_zendesk_ticket_id
                ? "Created"
                : item.existing_zendesk_ticket_id
                ? "Skipped"
                : null,
            }));

            console.log('Adjusted Nms Alerts : Zendesk resposne--', adjustedData);
            // Return the adjusted response
      return { ...response, data: adjustedData };
      //return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);

//Use this getNmsLogsData method for actual api- nms logs
export const getNmsLogsData = createAsyncThunk(
  'filters/getNmsLogsData',
  async (
    {
      url,
      headers,
      page,
      selectedMenu,
    }: {
      url: string;
      headers: Record<string, string>;
      page: number;
      selectedMenu: string;
    },
    { getState, rejectWithValue },
  ) => {
    const filterState = getState().filters;
    const { selectedFilter } = filterState;

    const data = filterState[selectedMenu];
    const {
      account,
      start,
      end,
      sort,
      requester,
      alertId,
      brands,
      //store,
      timeStamp,
    } = selectedFilter;
    let startDate = start;
    const params = {
      account: brands,
      //account,
      page,
      sort,
      start: startDate,
      //start: startDate.split("T")[0]
      end,
      requester,
      alertId,
      brands,
      //store,
      timeStamp,
    };

    const flattenNmsLogsData = (data) => {
      // Convert the object with numeric keys to an array of values
      const entries = Object.values(data);
    
      return entries.flatMap(entry =>
        entry.results.map(result => ({
          alert_id: entry.alert_id,
          job_id: entry.job_id,
          // Extract logical database name after 'ZOOM_'
          logical_database: entry.logical_database.split('ZOOM_')[1] || entry.logical_database,
          // Format timestamps
          bq_created: new Date(entry.bq_created).toISOString().slice(0, 16),
          alert_message: result.alert_message,
          alert_timestamp: new Date(result.alert_timestamp).toISOString().slice(0, 16),
          estation_name: result.estation_name,
          status: entry.status,
        }))
      );
    };
        
    try {
      const response = await fetchDataWithParams(url, headers, params);
      console.log('Nms logs resposne--', response);
      
      // Check if response is a JSON string and parse it if necessary
      let data = typeof response === 'string' ? JSON.parse(response) : response;

      // Handle cases where the response object might have a "data" field within a "data" field
      if (data?.data) {
         data = data.data;
      }
      console.log('NMSLOGSDATA--', data);
      //const transformedData = flattenNmsLogsData(Object.values(data || {}));
      //const transformedData = flattenNmsLogsData(data);
      //return transformedData;
      return data;
    } catch (error) {
      console.error(error);
      return rejectWithValue('No data Found');
    }
  },
);

const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setConfig(state, action) {
      state.config = action.payload;
    },
    setClientType(state, action) {
      state.clientType = action.payload;
    },
    setShowTable(state, action) {
      state.showTable = action.payload;
    },
    setIsExportToCsv(state, action) {
      state.isExportToCsv = action.payload;
    },
    setShowTrendLine(state, action) {
      state.showTrendLine = action.payload;
    },
    updateClientConfig(state, action) {
      state.clientConfig = action.payload;
    },
    updateSelectedFilter(
      state,
      action: PayloadAction<{ key: string; value: string }>,
    ) {
      const { key, value } = action.payload;
      if (state.selectedFilter.hasOwnProperty(key)) {
        state.selectedFilter[key] = value;
      }
    },
    // Optionally, you can reset the filter to initial state
    resetSelectedFilter(state) {
      state.selectedFilter = { ...initialState.selectedFilter };
      // const { startDate, endDate } = getDateRange('last 30 days');
      const { startDate, endDate } = state.clientType === 'NMS' ? getDateRange('last 7 days'): getDateRange('last 30 days');
      state.selectedFilter['start'] = startDate;
      state.selectedFilter['end'] = endDate + 'T23:59:59';
      //check if we need this for nms dash?
      if ('account' in state.selectedFilter) {
        state.selectedFilter['account'] = state.clientType === 'NMS' ? '' : state.selectedFilter.account;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)
      .addCase(getFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFilters.fulfilled, (state, action) => {
        const { Channels, MachineTypes, Planograms } = action.payload;
        if (action.meta.arg.type === '') {
          state.channels = Channels;
          state.machineTypes = MachineTypes;
          state.planograms = Planograms;
        }
        if (action.meta.arg.type === 'stores') {
          state.locations = action.payload;
        }
        if (action.meta.arg.type === 'products') {
          state.products = action.payload;
        }
        state.loading = false;
      })
      .addCase(getFilters.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch filters ' + action.meta.arg.url;
      })
      .addCase(getActiveUsers.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on new request
      })
      .addCase(
        getActiveUsers.fulfilled,
        (state, action: PayloadAction<UserCollection>) => {
          state.loading = false;
          const { selectedMenu } = action.meta.arg;
          state.users = action.payload; // Set the active users
          const tData = transformUserCollection(action.payload);
          state[selectedMenu]['data'] = tData;
          state.admin['data'] = tData;
        },
      )
      .addCase(getActiveUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load active users'; // Set error message
      })
      .addCase(getInvitationById.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset error on new request
      })
      .addCase(
        getInvitationById.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.loading = false;
          const { activationCode } = action.meta.arg;
          state.invite[activationCode] = action.payload;
        },
      )
      .addCase(getInvitationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load User Invitation'; // Set error message
      })
      .addCase(getTransactionData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransactionData.fulfilled, (state, action) => {
        const data = action.payload;

        if (data && data.length > 0) {
          AppConfig.transactions.map((value, index) => {
            state.transactions[value] = data[index];
          });
        }

        state.loading = false;
      })
      .addCase(getTransactionData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch Transactions ' + action.meta.arg.url;
      })
      .addCase(getInterActionsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInterActionsData.fulfilled, (state, action) => {
        const data = action.payload;
        if (data && data.length > 0) {
          AppConfig.interActions.map((value, index) => {
            state.interactions[value] = data[index];
          });
        }

        state.loading = false;
      })
      .addCase(getInterActionsData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch Interactions ' + action.meta.arg.url;
      })
      .addCase(getNetOpsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNetOpsData.fulfilled, (state, action) => {
        const data = action.payload;
        if (data && data.length > 0) {
          AppConfig.netOps.map((value, index) => {
            state.netOps[value] = data[index];
          });
        }

        state.loading = false;
      })
      .addCase(getNetOpsData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch Netops ' + action.meta.arg.url;
      })
      .addCase(getInventoryData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInventoryData.fulfilled, (state, action) => {
        const { selectedMenu } = action.meta.arg;
        if (
          state[selectedMenu] &&
          state[selectedMenu].data &&
          state[selectedMenu].data.length > 0
        ) {
          state[selectedMenu].data = [
            //...state[selectedMenu].data,
            ...action.payload.data,
          ];
          state[selectedMenu].pagination = action.payload.pagination;
        } else {
          state[selectedMenu] = action.payload;
        }

        state.loading = false;
      })
      .addCase(getInventoryData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch Netops ' + action.meta.arg.url;
      })
      .addCase(getReportsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReportsData.fulfilled, (state, action) => {
        const { selectedMenu, selectedTile } = action.meta.arg;
        if (
          state[selectedMenu] &&
          state[selectedMenu][selectedTile] &&
          state[selectedMenu][selectedTile].data &&
          state[selectedMenu][selectedTile].data.length > 0
        ) {
          state[selectedMenu][selectedTile].data = [
            // ...state[selectedMenu][selectedTile].data,
            ...action.payload.data,
          ];
          state[selectedMenu][selectedTile].pagination =
            action.payload.pagination;
        } else {
          state[selectedMenu][selectedTile] = action.payload;
        }

        state.loading = false;
      })
      .addCase(getReportsData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch Netops ' + action.meta.arg.url;
      })
      .addCase(getStoreIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStoreIds.fulfilled, (state, action) => {
        const storeDetails  = action.payload.data;
        console.log("Store Details:", storeDetails);
      // Transform the data to keep only `concept_name` and `estation_name`
       const transformedData = Array.isArray(storeDetails)
         ? storeDetails.map((item: any) => ({
            concept_name: item.concept_name,
            estation_name: item.estation_name,
          }))
        : [];

        state.storeIdDetails = transformedData; 
        console.log("Stored Data in State:", state.storeIdDetails);

        state.loading = false;
      })
      .addCase(getStoreIds.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch StoreIds ' + action.meta.arg.url;
      })
      .addCase(getReportsNMSWorker.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReportsNMSWorker.fulfilled, (state, action) => {
        const { selectedMenu } = action.meta.arg;
        if (
          state[selectedMenu] &&
          state[selectedMenu].data &&
          state[selectedMenu].data.length > 0
        ) {
          state[selectedMenu].data = [
            // ...state[selectedMenu][selectedTile].data,
            ...action.payload.data,
          ];
          state[selectedMenu].pagination =
            action.payload.pagination;
        } else {
          state[selectedMenu] = action.payload;
        }

        state.loading = false;
      })
      .addCase(getReportsNMSWorker.rejected, (state, action) => {
        state.loading = false;
        const { selectedMenu } = action.meta.arg;

        // Optional: Clear only if a fetch had been attempted
        if (state[selectedMenu]) {
          state[selectedMenu].data = [];
          state[selectedMenu].pagination = null;
        }
        state.error =
          action.error.message ||
          'Failed to fetch Nms Worker Logs ' + action.meta.arg.url;
      })
      .addCase(getNmsAlertsZendeskData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNmsAlertsZendeskData.fulfilled, (state, action) => {
        const { selectedMenu } = action.meta.arg;
        if (
          state[selectedMenu] &&
          state[selectedMenu].data &&
          state[selectedMenu].data.length > 0
        ) {
          state[selectedMenu].data = [
            // ...state[selectedMenu][selectedTile].data,
            ...action.payload.data,
          ];
          state[selectedMenu].pagination =
            action.payload.pagination;
        } else {
          state[selectedMenu] = action.payload;
        }

        state.loading = false;
      })
      .addCase(getNmsAlertsZendeskData.rejected, (state, action) => {
        state.loading = false;
        const { selectedMenu } = action.meta.arg;

        // Optional: Clear only if a fetch had been attempted
        if (state[selectedMenu]) {
          state[selectedMenu].data = [];
          state[selectedMenu].pagination = null;
        }
        state.error =
          action.error.message ||
          'Failed to fetch Nms Alerts : Zendesk data ' + action.meta.arg.url;
      })
      .addCase(getNmsLogsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNmsLogsData.fulfilled, (state, action) => {
        const { selectedMenu } = action.meta.arg;
        if (
          state[selectedMenu] &&
          state[selectedMenu].data &&
          state[selectedMenu].data.length > 0
        ) {
          state[selectedMenu].data = [
            // ...state[selectedMenu][selectedTile].data,
            ...action.payload.data,
          ];
          state[selectedMenu].pagination =
            action.payload.pagination;
        } else {
          state[selectedMenu] = action.payload;
        }

        state.loading = false;
      })
      .addCase(getNmsLogsData.rejected, (state, action) => {
        state.loading = false;
        const { selectedMenu } = action.meta.arg;

        // Optional: Clear only if a fetch had been attempted
        if (state[selectedMenu]) {
          state[selectedMenu] = {
            data: [],
            pagination: null,
          };
        }
        state.error =
          action.error.message ||
          'Failed to fetch Nms Logs data ' + action.meta.arg.url;
      });
  },
});
export const {
  setConfig,
  updateSelectedFilter,
  updateClientConfig,
  resetSelectedFilter,
  setClientType,
  setShowTable,
  setIsExportToCsv,
  setShowTrendLine,
} = filterSlice.actions;

export default filterSlice.reducer;
